import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import * as investmentService from "../services/investmentService";
import * as projectInvestmentService from "../services/projectInvestmentService";
import * as projectService from "../services/projectService";
import type { UserInvestment, ProjectInvestment, Project } from "../types/api";
import { Skeleton } from "../components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { pickPackName, pickProjectName } from "../utils/localeDisplay";
import {
  Calendar,
  TrendingUp,
  Package,
  DollarSign,
  Building2,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Pagination } from "../components/table/DataTable";

const InvestmentsPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [investments, setInvestments] = useState<UserInvestment[]>([]);
  const [projectInvestments, setProjectInvestments] = useState<
    ProjectInvestment[]
  >([]);
  const [projects, setProjects] = useState<Map<number, Project>>(new Map());
  const [loading, setLoading] = useState(true);
  const [packPage, setPackPage] = useState(1);
  const [projectPage, setProjectPage] = useState(1);
  const [totalPacks, setTotalPacks] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const investmentsPerPage = 10;
  const totalPackPages = Math.max(
    1,
    Math.ceil(totalPacks / investmentsPerPage)
  );
  const totalProjectPages = Math.max(
    1,
    Math.ceil(totalProjects / investmentsPerPage)
  );

  useEffect(() => {
    const fetchInvestments = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const packOffset = (packPage - 1) * investmentsPerPage;
        const projectOffset = (projectPage - 1) * investmentsPerPage;

        // Charger les investissements dans les packs
        const packData = await investmentService.getUserInvestments({
          limit: investmentsPerPage,
          offset: packOffset,
        });
        setInvestments(packData);

        // Charger les investissements dans les projets
        const projectData =
          await projectInvestmentService.getUserProjectInvestments(
            investmentsPerPage,
            projectOffset
          );
        setProjectInvestments(projectData);

        // Charger les détails des projets
        const projectIds = [
          ...new Set(projectData.map((inv) => inv.projectId)),
        ];
        const projectsMap = new Map<number, Project>();
        for (const projectId of projectIds) {
          try {
            const project = await projectService.getProjectById(projectId);
            projectsMap.set(projectId, project);
          } catch (err) {
            console.error(
              `Erreur lors du chargement du projet ${projectId}:`,
              err
            );
          }
        }
        setProjects(projectsMap);

        // TODO: Le backend devrait retourner le total pour la pagination
        if (packData.length === investmentsPerPage) {
          setTotalPacks(packPage * investmentsPerPage + 1); // Estimation
        } else {
          setTotalPacks((packPage - 1) * investmentsPerPage + packData.length);
        }

        if (projectData.length === investmentsPerPage) {
          setTotalProjects(projectPage * investmentsPerPage + 1); // Estimation
        } else {
          setTotalProjects(
            (projectPage - 1) * investmentsPerPage + projectData.length
          );
        }
      } catch (error) {
        console.error("Erreur lors du chargement des investissements:", error);
      } finally {
        setLoading(false);
      }
    };
    void fetchInvestments();
  }, [user, packPage, projectPage]);

  useEffect(() => {
    if (packPage > totalPackPages) {
      setPackPage(totalPackPages);
    }
  }, [packPage, totalPackPages]);

  useEffect(() => {
    if (projectPage > totalProjectPages) {
      setProjectPage(totalProjectPages);
    }
  }, [projectPage, totalProjectPages]);

  // Fonction pour calculer les gains réels en fonction de la durée écoulée
  const calculateRealGains = (
    investment: ProjectInvestment,
    project: Project | undefined
  ): {
    currentGain: number;
    progressPercent: number;
    daysRemaining: number;
    isMature: boolean;
  } => {
    if (!investment.maturityDate) {
      return {
        currentGain: 0,
        progressPercent: 0,
        daysRemaining: 0,
        isMature: false,
      };
    }

    const now = new Date();
    const investedAt = new Date(investment.investedAt);
    const maturityDate = new Date(investment.maturityDate);

    const totalDays = Math.ceil(
      (maturityDate.getTime() - investedAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysElapsed = Math.floor(
      (now.getTime() - investedAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysRemaining = Math.max(
      0,
      Math.ceil(
        (maturityDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      )
    );

    const isMature = now >= maturityDate;
    const progressPercent =
      totalDays > 0
        ? Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100))
        : 100;

    // Calcul du gain réel : proportionnel au temps écoulé
    // Si l'investissement est arrivé à maturité, le gain est complet
    const currentGain = isMature
      ? investment.expectedReturn
      : (investment.expectedReturn * progressPercent) / 100;

    return { currentGain, progressPercent, daysRemaining, isMature };
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl">
          <p className="text-slate-600 dark:text-white/70">{t("investments.loginRequired")}</p>
        </div>
      </div>
    );
  }

  const totalInvested =
    investments.reduce((sum, inv) => sum + inv.amount, 0) +
    projectInvestments.reduce((sum, inv) => sum + inv.amount, 0);
  const activeInvestments = investments.filter(
    (inv) => inv.status === "active"
  );
  const activeProjectInvestments = projectInvestments.filter(
    (inv) => inv.status === "active"
  );
  const activeTotal =
    activeInvestments.reduce((sum, inv) => sum + inv.amount, 0) +
    activeProjectInvestments.reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
            {t("investments.title")}
          </h1>
          <p className="text-slate-600 dark:text-white/70">{t("investments.subtitle")}</p>
        </div>

        {/* Statistiques */}
        <div className="grid gap-4 md:grid-cols-2 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30 p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              <span className="text-sm text-slate-500 dark:text-white/60">
                {t("investments.totalInvested")}
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {totalInvested.toFixed(2)} USD
              </p>
            )}
          </div>
          {/* <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="h-5 w-5 text-blue-400" />
              <span className="text-sm text-slate-500 dark:text-white/60">
                {t("investments.activeInvestments")}
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {activeInvestments.length}
              </p>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30 p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-purple-400" />
              <span className="text-sm text-slate-500 dark:text-white/60">
                {t("investments.activeTotal")}
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {activeTotal.toFixed(2)} USD
              </p>
            )}
          </div> */}
        </div>

        {/* Liste des investissements dans les projets */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            {t(
              "investments.projectInvestments",
              "Mes investissements dans les projets"
            )}
          </h2>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-2xl" />
              ))}
            </div>
          ) : projectInvestments.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/80 p-12 text-center dark:border-white/5 dark:from-slate-900/60 dark:to-slate-800/40">
              <p className="text-lg text-slate-600 dark:text-white/70">
                {t(
                  "investments.noProjectInvestments",
                  "Aucun investissement dans les projets pour le moment."
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {projectInvestments.map((investment) => {
                const project = projects.get(investment.projectId);
                // Utiliser les valeurs calculées du backend si disponibles, sinon calculer côté frontend
                const gains = calculateRealGains(investment, project);
                const currentGain = investment.currentGain ?? gains.currentGain;
                const progressPercent =
                  investment.progressPercent ?? gains.progressPercent;
                const daysRemaining =
                  investment.daysRemaining ?? gains.daysRemaining;
                const isMature = investment.isMature ?? gains.isMature;
                const projectName = project
                  ? pickProjectName(project, i18n.language)
                  : `Projet #${investment.projectId}`;

                return (
                  <div
                    key={`project-${investment.id}`}
                    className="group relative rounded-2xl border border-slate-200 bg-gradient-to-br from-cyan-50/80 to-blue-50/80 backdrop-blur-sm p-6 shadow-lg shadow-slate-200/50 transition-all duration-300 hover:shadow-xl dark:border-white/10 dark:from-cyan-900/20 dark:to-blue-900/20 dark:shadow-black/20 dark:hover:shadow-black/40 overflow-hidden">
                    <div className="relative z-10">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="h-5 w-5 text-cyan-400" />
                            <div>
                              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                                {projectName}
                              </h3>
                              <Link
                                to={`/projects/${investment.projectId}`}
                                className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 inline-block">
                                {t("investments.viewProject", "Voir le projet")}{" "}
                                →
                              </Link>
                            </div>
                          </div>

                          {/* Barre de progression */}
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-white/60 mb-1">
                              <span>
                                {t("investments.progress", "Progression")}
                              </span>
                              <span>{progressPercent.toFixed(1)}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  isMature
                                    ? "bg-emerald-500"
                                    : "bg-gradient-to-r from-cyan-400 to-blue-500"
                                }`}
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                            {investment.amount.toFixed(2)} {investment.currency}
                          </p>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <TrendingUp className="h-4 w-4 text-emerald-400" />
                              <span className="text-emerald-400 font-semibold">
                                +{currentGain.toFixed(2)} {investment.currency}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-white/60">
                                (
                                {(
                                  (currentGain / investment.amount) *
                                  100
                                ).toFixed(2)}
                                %)
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white/60">
                              <Clock className="h-3 w-3" />
                              <span>
                                {isMature
                                  ? t("investments.mature", "Arrivé à maturité")
                                  : daysRemaining > 0
                                  ? t(
                                      "investments.daysRemaining",
                                      "{{days}} jours restants",
                                      { days: daysRemaining }
                                    )
                                  : t("investments.completed", "Terminé")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white/60">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {new Date(
                                  investment.investedAt
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {projectInvestments.length > 0 && totalProjectPages > 1 && (
            <div className="mt-6 border-t border-slate-200 pt-3 dark:border-white/10">
              <Pagination
                currentPage={projectPage}
                totalPages={totalProjectPages}
                totalItems={totalProjects}
                itemsPerPage={investmentsPerPage}
                onPageChange={setProjectPage}
                isLoading={loading}
                ariaLabelPrevious={t("investments.pagination.previous")}
                ariaLabelNext={t("investments.pagination.next")}
              />
            </div>
          )}
        </div>

        {/* Section des packs (crédits de compte) */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {t("investments.packsTitle", "Packs achetés (crédit de compte)")}
          </h2>
          <p className="text-sm text-slate-500 dark:text-white/60 mb-4">
            {t(
              "investments.packsDescription",
              "Les packs permettent de créditer votre compte principal. Ce ne sont pas des investissements dans des projets."
            )}
          </p>
          {loading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-32 rounded-2xl" />
              ))}
            </div>
          ) : investments.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/80 p-12 text-center dark:border-white/5 dark:from-slate-900/60 dark:to-slate-800/40">
              <p className="text-lg text-slate-600 dark:text-white/70">
                {t("investments.noPacks", "Aucun pack acheté pour le moment.")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {investments.map((investment) => {
                const pack = investment.pack;
                const displayName = pack
                  ? pickPackName(pack, i18n.language)
                  : "N/A";
                const bgGradient =
                  pack?.primaryColor && pack?.secondaryColor
                    ? `linear-gradient(135deg, ${pack.primaryColor} 0%, ${pack.secondaryColor} 100%)`
                    : "linear-gradient(135deg, rgb(51 65 85 / 0.5) 0%, rgb(30 41 59 / 0.3) 100%)";

                return (
                  <div
                    key={investment.id}
                    className="group relative rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm p-6 shadow-lg shadow-slate-200/50 transition-all duration-300 hover:shadow-xl dark:border-white/10 dark:bg-transparent dark:shadow-black/20 dark:hover:shadow-black/40 overflow-hidden"
                    style={{ background: bgGradient }}>
                    <div className="relative z-10">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div>
                              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                                {displayName}
                              </h3>
                              {/* <div className="flex items-center gap-4 mt-1">
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  investment.status === "active"
                                    ? "bg-emerald-500/20 text-emerald-400"
                                    : investment.status === "completed"
                                    ? "bg-blue-500/20 text-blue-400"
                                    : investment.status === "pending"
                                    ? "bg-yellow-500/20 text-yellow-400"
                                    : "bg-gray-500/20 text-gray-400"
                                }`}>
                                {t(`investments.status.${investment.status}`)}
                              </span>
                            </div> */}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                            {investment.amount.toFixed(2)} {investment.currency}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white/60">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {new Date(
                                investment.investedAt
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {investments.length > 0 && totalPackPages > 1 && (
            <div className="mt-6 border-t border-slate-200 pt-3 dark:border-white/10">
              <Pagination
                currentPage={packPage}
                totalPages={totalPackPages}
                totalItems={totalPacks}
                itemsPerPage={investmentsPerPage}
                onPageChange={setPackPage}
                isLoading={loading}
                ariaLabelPrevious={t("investments.pagination.previous")}
                ariaLabelNext={t("investments.pagination.next")}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentsPage;
